import { getToken } from '../../components/authentication/functions/authentication_management';
import { SimulationRunSummary, RideSchema, SimulationActivity, UnmatchedRide, SimulationRunSettings, ResultsVehicleRow } from '../../types/simulation';
import {
  normalizeActivitiesFromApi,
  parseRidePointsFromSolution,
  parseActivitiesFromSolution,
} from './simulationTransforms';
import { runInWorker } from './simulationWorkerClient';
import { getSimulationApiBaseUrl } from '../getAtlasApiBaseUrl';

const API_BASE_URL = getSimulationApiBaseUrl();

export type ObjectAccessRole = 'Owner' | 'Editor' | 'Viewer';
export type ShareableRole = Exclude<ObjectAccessRole, 'Owner'>;

export interface ShareRequest {
  email: string;
  role: ShareableRole;
}

export interface ShareResponse {
  message: string;
  role: ShareableRole;
  shared_with: string;
}

export interface ShareListEntry {
  email: string;
  full_name: string;
  role: ObjectAccessRole;
  actor_id: string;
}

export interface ShareListResponse {
  shares: ShareListEntry[];
}

const authHeaders = () => {
  const token = getToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token.access_token}`,
  };
};

/**
 * Share simulation results with another user (upsert semantics).
 * POST /results/{results_id}/share
 */
export async function shareSimulationResults(resultsId: string, request: ShareRequest): Promise<ShareResponse> {
  const response = await fetch(`${API_BASE_URL}/results/${resultsId}/share`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to share results (${response.status}): ${errorText || response.statusText}`);
  }

  return (await response.json()) as ShareResponse;
}

/**
 * List shares for a results id.
 * GET /results/{results_id}/share
 */
export async function listSimulationResultsShares(resultsId: string): Promise<ShareListResponse> {
  const response = await fetch(`${API_BASE_URL}/results/${resultsId}/share`, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch shares (${response.status}): ${errorText || response.statusText}`);
  }

  return (await response.json()) as ShareListResponse;
}

/**
 * Revoke access to results for a target email.
 * DELETE /results/{results_id}/share with JSON body { email }
 */
export async function revokeSimulationResultsShare(resultsId: string, email: string): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/results/${resultsId}/share`, {
    method: 'DELETE',
    headers: authHeaders(),
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to revoke access (${response.status}): ${errorText || response.statusText}`);
  }

  return (await response.json()) as { message: string };
}

export const parseMetricsPayload = (payload: any): Partial<SimulationRunSummary> | null => {
  if (!payload) return null;
  if (Array.isArray(payload)) return payload[0] || null;
  if (Array.isArray(payload.data)) return payload.data[0] || null;
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return typeof payload === 'object' ? payload : null;
};

export async function fetchSimulationMetrics(resultsId: string): Promise<Partial<SimulationRunSummary> | null> {
  const response = await fetch(`${API_BASE_URL}/results/${resultsId}/metrics`, {
    method: 'GET',
    headers: authHeaders(),
    cache: 'default',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch metrics (${response.status}): ${errorText || response.statusText}`);
  }

  const json = await response.json();
  return parseMetricsPayload(json);
}

export async function fetchSimulationActivities(resultsId: string): Promise<SimulationActivity[]> {
  const response = await fetch(`${API_BASE_URL}/results/${resultsId}/activities`, {
    method: 'GET',
    headers: authHeaders(),
    cache: 'default',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch activities (${response.status}): ${errorText || response.statusText}`);
  }

  const json = await response.json();
  let raw: any[] = [];
  if (Array.isArray(json)) raw = json;
  else if (Array.isArray(json.data)) raw = json.data;
  else if (json.activities && Array.isArray(json.activities)) raw = json.activities;

  try {
    return await runInWorker('normalizeActivities', raw) as SimulationActivity[];
  } catch {
    return normalizeActivitiesFromApi(raw);
  }
}

function normalizeUnmatchedRideItem(raw: Record<string, unknown>): UnmatchedRide {
  const r = raw as Record<string, unknown>;
  const requestTime =
    (typeof r.request_time === 'string' && r.request_time) ||
    (typeof r.requested_time === 'string' && r.requested_time) ||
    (typeof r.pickup_time === 'string' && r.pickup_time) ||
    (typeof r.requestTime === 'string' && r.requestTime) ||
    undefined;
  return {
    ...r,
    ride_id:
      (typeof r.ride_id === 'string' && r.ride_id) ||
      (typeof r.rideId === 'string' && r.rideId) ||
      (typeof r.external_reference_id === 'string' && r.external_reference_id) ||
      undefined,
    request_id:
      (typeof r.request_id === 'string' && r.request_id) ||
      (typeof r.requestId === 'string' && r.requestId) ||
      undefined,
    request_time: requestTime,
    reason:
      (typeof r.reason === 'string' && r.reason) ||
      (typeof r.unassigned_reason === 'string' && r.unassigned_reason) ||
      (typeof r.rejection_reason === 'string' && r.rejection_reason) ||
      undefined,
  };
}

function normalizeUnmatchedRidesPayload(json: unknown): UnmatchedRide[] {
  if (!json) return [];
  let raw: unknown[] = [];
  if (Array.isArray(json)) raw = json;
  else if (json && typeof json === 'object') {
    const o = json as Record<string, unknown>;
    if (Array.isArray(o.data)) raw = o.data;
    else if (Array.isArray(o.unmatched_rides)) raw = o.unmatched_rides;
    else if (Array.isArray(o.unmatchedRides)) raw = o.unmatchedRides;
    else if (Array.isArray(o.rides)) raw = o.rides;
  }
  return raw
    .filter((item): item is Record<string, unknown> => item != null && typeof item === 'object')
    .map((item) => normalizeUnmatchedRideItem(item));
}

function isNoUnmatchedRidesDetailPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const detail = (payload as Record<string, unknown>).detail;
  return typeof detail === 'string' && /no unmatched rides found/i.test(detail);
}

/**
 * Fetch rides that could not be allocated for this results id.
 * GET /results/{results_id}/unmatched_rides
 */
export async function fetchUnmatchedRides(resultsId: string): Promise<UnmatchedRide[]> {
  const response = await fetch(`${API_BASE_URL}/results/${resultsId}/unmatched_rides`, {
    method: 'GET',
    headers: authHeaders(),
  });

  // Simulator returns 404 with a detail message when there are zero unmatched rides — treat as empty.
  if (response.status === 404) {
    return [];
  }

  if (!response.ok) {
    // Some environments return the "No unmatched rides found..." message
    // with non-404 statuses. Normalize that to an empty list as well.
    try {
      const json = await response.clone().json();
      if (isNoUnmatchedRidesDetailPayload(json)) return [];
    } catch {
      // Ignore JSON parse errors and fall back to text.
    }

    const errorText = await response.text();
    if (/no unmatched rides found/i.test(errorText)) return [];
    throw new Error(`Failed to fetch unmatched rides (${response.status}): ${errorText || response.statusText}`);
  }

  const json = await response.json();
  if (isNoUnmatchedRidesDetailPayload(json)) return [];
  return normalizeUnmatchedRidesPayload(json);
}

// Re-export for callers that import these directly from resultsApi
export { parseRidePointsFromSolution, parseActivitiesFromSolution, buildRidePointsFromActivities } from './simulationTransforms';

/**
 * Module-level in-flight cache for /integration/solution/{resultsId}.
 * Concurrent callers for the same ID share a single network request.
 * The entry is evicted once the promise settles so subsequent independent
 * calls still hit the network (browser HTTP cache handles re-use after that).
 */
const _solutionInFlight = new Map<string, Promise<any>>();

async function fetchSolutionPayload(resultsId: string): Promise<any> {
  if (!_solutionInFlight.has(resultsId)) {
    const promise = fetch(`${API_BASE_URL}/integration/solution/${resultsId}`, {
      method: 'GET',
      headers: authHeaders(),
      cache: 'default',
    })
      .then(async (res) => {
        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(`Failed to fetch solution (${res.status}): ${errorText || res.statusText}`);
        }
        return res.json();
      })
      .finally(() => _solutionInFlight.delete(resultsId));
    _solutionInFlight.set(resultsId, promise);
  }
  return _solutionInFlight.get(resultsId)!;
}

export async function fetchSimulationRidePoints(resultsId: string): Promise<RideSchema[] | null> {
  const json = await fetchSolutionPayload(resultsId);
  const payload = json.solution || json.data || json;
  try {
    const rides = await runInWorker('parseRidePoints', payload) as RideSchema[];
    return rides.length ? rides : null;
  } catch {
    const rides = parseRidePointsFromSolution(payload);
    return rides.length ? rides : null;
  }
}

/**
 * Fetch the solution once and return both ride points and activities for the Operations view.
 * Activities come from solution route activities with "type" (START, PICK, DROP).
 * Shares the same in-flight request as fetchSimulationRidePoints for the same resultsId.
 */
export async function fetchSolutionResults(resultsId: string): Promise<{
  ridePoints: RideSchema[] | null;
  activities: SimulationActivity[];
}> {
  const json = await fetchSolutionPayload(resultsId);
  const payload = json.solution || json.data || json;
  try {
    const [ridePoints, activities] = await Promise.all([
      runInWorker('parseRidePoints', payload) as Promise<RideSchema[]>,
      runInWorker('parseActivitiesFromSolution', payload) as Promise<SimulationActivity[]>,
    ]);
    return { ridePoints: ridePoints.length ? ridePoints : null, activities };
  } catch {
    const ridePoints = parseRidePointsFromSolution(payload);
    const activities = parseActivitiesFromSolution(payload);
    return { ridePoints: ridePoints.length ? ridePoints : null, activities };
  }
}

/**
 * Fetches the simulation configuration (scenario metadata, solver config, and runtime settings)
 * for a given result from GET /results/{results_id}/settings.
 *
 * Parameters:
 *   resultsId (string): The UUID of the simulation result.
 *
 * Returns:
 *   Promise<SimulationRunSettings>
 */
export async function fetchSimulationSettings(resultsId: string): Promise<SimulationRunSettings> {
  const response = await fetch(`${API_BASE_URL}/results/${resultsId}/settings`, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch settings (${response.status}): ${errorText || response.statusText}`);
  }

  const json = await response.json();
  return json.data as SimulationRunSettings;
}

/**
 * Fetches vehicle rows for a simulation result from GET /results/{results_id}/vehicles.
 * Returns one flat row per vehicle-shift-capacity combination.
 */
export async function fetchSimulationVehicles(resultsId: string): Promise<ResultsVehicleRow[]> {
  const response = await fetch(`${API_BASE_URL}/results/${resultsId}/vehicles`, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (response.status === 404) {
    return [];
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch vehicles (${response.status}): ${errorText || response.statusText}`);
  }

  const json = await response.json();
  if (Array.isArray(json)) return json as ResultsVehicleRow[];
  if (Array.isArray(json.data)) return json.data as ResultsVehicleRow[];
  return [];
}
